import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, FlaskConical, LayoutGrid, List, Search, TrendingUp, X, Zap } from 'lucide-react';
import { api } from '../api';
import { CourseCard } from '../components/common/CourseCard';
import { CourseDetailModal } from '../components/modals/CourseDetailModal';
import { ScheduleWorkshopModal } from '../components/modals/ScheduleWorkshopModal';
import { PastDueReminder } from '../components/widgets/PastDueReminder';

const CourseBrowser = ({ user, onNavigate, onTermsAccept }) => {
  const { t } = useTranslation();
  const [courses, setCourses] = useState([]);
  const [metadata, setMetadata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    category: '', solution_area: '', level: '', course_type: '', language: '', search: '', tag: ''
  });
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [workshops, setWorkshops] = useState([]);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [preselectedCourse, setPreselectedCourse] = useState(null);
  const [viewMode, setViewMode] = useState('card'); // 'card' | 'list'

  // Smart Search state
  const [smartSearchMode, setSmartSearchMode] = useState(false);
  const [smartQuery, setSmartQuery] = useState('');
  const [smartResults, setSmartResults] = useState(null);
  const [smartLoading, setSmartLoading] = useState(false);
  const [smartError, setSmartError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const endpoint = ['training_partner', 'partner_admin'].includes(user.role)
          ? '/courses/with-download-info'
          : '/courses';
        const [coursesData, metaData] = await Promise.all([
          api.request(endpoint),
          api.request('/metadata')
        ]);
        setCourses(Array.isArray(coursesData) ? coursesData : coursesData.courses || []);
        setMetadata(metaData);
        if (['training_partner', 'partner_admin'].includes(user.role)) {
          try {
            const workshopsData = await api.request('/workshops');
            setWorkshops(workshopsData || []);
          } catch (e) { console.error('Failed to fetch workshops:', e); }
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user.role]);

  const fetchCourses = async () => {
    if (smartSearchMode) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => { if (value) params.append(key, value); });
      const endpoint = ['training_partner', 'partner_admin'].includes(user.role)
        ? '/courses/with-download-info'
        : '/courses';
      const data = await api.request(`${endpoint}?${params}`);
      setCourses(Array.isArray(data) ? data : data.courses || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(fetchCourses, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const hasAccess = () => {
    if (['admin', 'content_admin', 'ms_stakeholder'].includes(user.role)) return true;
    return user.is_approved === true;
  };

  const handleSmartSearch = async () => {
    if (!smartQuery.trim()) return;
    setSmartLoading(true);
    setSmartError('');
    setSmartResults(null);
    try {
      const result = await api.request('/search/smart', {
        method: 'POST',
        body: JSON.stringify({ query: smartQuery })
      });
      setSmartResults(result);
    } catch (err) {
      setSmartError(err.message || 'Smart search failed. Please try again.');
    } finally {
      setSmartLoading(false);
    }
  };

  const clearSmartSearch = () => {
    setSmartResults(null);
    setSmartQuery('');
    setSmartError('');
    setSmartSearchMode(false);
  };

  const handleTagClick = (tag) => {
    setSmartSearchMode(false);
    setSmartResults(null);
    setSmartQuery('');
    setFilters(f => ({ ...f, tag, search: '' }));
  };

  const clearTagFilter = () => setFilters(f => ({ ...f, tag: '' }));

  const FilterDropdown = ({ label, options, value, onChange }) => (
    <div className="relative">
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="appearance-none bg-white border border-gray-200 rounded-lg px-4 py-2 pr-8 text-sm focus:ring-2 focus:ring-levelup-accent focus:border-transparent">
        <option value="">{label}</option>
        {options?.map(opt => <option key={opt} value={opt}>{opt}</option>)}
      </select>
      <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={16} />
    </div>
  );

  const displayCourses = smartResults ? smartResults.courses : courses;
  const isSmartResultsActive = smartSearchMode && smartResults;

  return (
    <div className="space-y-4">
      <div className="bg-levelup-dark rounded-xl p-4 text-white">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-xl font-bold">{t('courses.title')}</h1>
            <p className="text-gray-300 mt-1">{t('courses.subtitle')}</p>
          </div>
          <button
            onClick={() => { setSmartSearchMode(!smartSearchMode); setSmartResults(null); setSmartQuery(''); setSmartError(''); }}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
              smartSearchMode
                ? 'bg-levelup-accent text-levelup-darker'
                : 'bg-white/10 text-white hover:bg-white/20'
            }`}
            data-testid="smart-search-toggle"
          >
            <Zap size={16} />
            <span>AI Search</span>
          </button>
        </div>
      </div>

      {/* Past Due Workshops Reminder */}
      {['training_partner', 'partner_admin'].includes(user.role) && (
        <PastDueReminder workshops={workshops} onNavigate={onNavigate} />
      )}

      {/* Spotlight Workload Banners */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Microsoft Copilot Banner */}
        <div className="relative bg-gradient-to-br from-levelup-darker to-levelup-dark rounded-xl p-4 border border-levelup-teal/40 overflow-hidden group hover:border-levelup-accent/60 transition-all">
          <div className="absolute top-0 right-0 w-40 h-40 bg-levelup-accent/10 rounded-full -translate-y-20 translate-x-20 group-hover:bg-levelup-accent/15 transition-all" />
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 bg-levelup-accent/20 rounded-lg flex items-center justify-center">
                <Zap className="text-levelup-accent" size={18} />
              </div>
              <span className="text-xs font-bold text-levelup-accent uppercase tracking-wider">Featured Workload</span>
            </div>
            <h3 className="text-lg font-bold text-white mb-2">Microsoft Copilot</h3>
            <p className="text-sm text-gray-300 mb-4 leading-relaxed">Upskill partners on Microsoft 365 Copilot and AI agent capabilities. Drive productivity transformation and accelerate AI adoption across your customer base.</p>
            <button
              onClick={() => { setSmartSearchMode(false); setSmartResults(null); setFilters(f => ({...f, search: 'Copilot'})); }}
              className="inline-flex items-center px-4 py-2 bg-levelup-accent text-levelup-darker rounded-lg font-semibold text-sm hover:bg-levelup-accent-hover transition-colors"
              data-testid="banner-copilot-explore"
            >
              Explore Trainings <ChevronRight size={16} className="ml-1" />
            </button>
          </div>
        </div>

        {/* Frontier Transformation Banner */}
        <div className="relative bg-gradient-to-br from-blue-950 to-blue-900 rounded-xl p-4 border border-blue-700/40 overflow-hidden group hover:border-blue-500/60 transition-all">
          <div className="absolute top-0 right-0 w-40 h-40 bg-blue-400/10 rounded-full -translate-y-20 translate-x-20 group-hover:bg-blue-400/15 transition-all" />
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 bg-blue-400/20 rounded-lg flex items-center justify-center">
                <TrendingUp className="text-blue-300" size={18} />
              </div>
              <span className="text-xs font-bold text-blue-300 uppercase tracking-wider">Featured Workload</span>
            </div>
            <h3 className="text-lg font-bold text-white mb-2">Frontier Transformation</h3>
            <p className="text-sm text-gray-300 mb-4 leading-relaxed">Accelerate Agentic AI, Azure Cloud & AI Platform, and Microsoft Fabric capabilities. Build next-generation solutions with cutting-edge Azure technologies.</p>
            <button
              onClick={() => { setSmartSearchMode(false); setSmartResults(null); setFilters(f => ({...f, search: 'Azure'})); }}
              className="inline-flex items-center px-4 py-2 bg-blue-400 text-blue-950 rounded-lg font-semibold text-sm hover:bg-blue-300 transition-colors"
              data-testid="banner-frontier-explore"
            >
              Explore Trainings <ChevronRight size={16} className="ml-1" />
            </button>
          </div>
        </div>
      </div>

      {/* Smart Search Bar */}
      {smartSearchMode ? (
        <div className="bg-white rounded-xl shadow-md p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Zap className="text-levelup-accent" size={18} />
            <span className="text-sm font-semibold text-levelup-dark">AI-Powered Smart Search</span>
            <span className="text-xs text-gray-400 ml-1">— describe what you're looking for in plain language</span>
          </div>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder='e.g. "beginner Azure sales courses" or "hands-on lab for Copilot"'
                className="w-full pl-4 pr-4 py-3 border-2 border-levelup-accent/40 rounded-lg focus:ring-2 focus:ring-levelup-accent focus:border-transparent text-sm"
                value={smartQuery}
                onChange={(e) => setSmartQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSmartSearch()}
                data-testid="smart-search-input"
                autoFocus
              />
            </div>
            <button
              onClick={handleSmartSearch}
              disabled={smartLoading || !smartQuery.trim()}
              className="px-6 py-3 bg-levelup-accent text-levelup-darker rounded-lg font-semibold text-sm hover:bg-levelup-accent-hover transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              data-testid="smart-search-btn"
            >
              {smartLoading ? (
                <><div className="animate-spin w-4 h-4 border-2 border-levelup-darker border-t-transparent rounded-full" /><span>Searching...</span></>
              ) : (
                <><Search size={16} /><span>Search</span></>
              )}
            </button>
            <button onClick={clearSmartSearch}
              className="px-3 py-3 text-gray-400 hover:text-gray-600 rounded-lg border border-gray-200 hover:bg-gray-50 transition-all"
              data-testid="smart-search-clear" title="Back to regular search">
              <X size={16} />
            </button>
          </div>
          {smartError && (
            <p className="text-red-500 text-sm mt-2">{smartError}</p>
          )}
        </div>
      ) : (
        /* Regular Filters */
        <div className="bg-white rounded-xl shadow-md p-4">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input type="text" placeholder={t('courses.searchPlaceholder')}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-levelup-accent focus:border-transparent"
                value={filters.search}
                onChange={(e) => setFilters({...filters, search: e.target.value})}
                data-testid="search-input" />
            </div>
            <FilterDropdown label={t('courses.solutionArea')} options={metadata?.solution_areas}
              value={filters.solution_area} onChange={(v) => setFilters({...filters, solution_area: v})} />
            <FilterDropdown label={t('courses.level')} options={metadata?.levels}
              value={filters.level} onChange={(v) => setFilters({...filters, level: v})} />
            <FilterDropdown label={t('courses.courseType')} options={metadata?.course_types}
              value={filters.course_type} onChange={(v) => setFilters({...filters, course_type: v})} />
            <FilterDropdown label={t('courses.language')} options={metadata?.languages}
              value={filters.language} onChange={(v) => setFilters({...filters, language: v})} />
            {/* Active tag chip */}
            {filters.tag && (
              <button
                onClick={clearTagFilter}
                className="flex items-center gap-1.5 bg-teal-100 text-teal-700 border border-teal-300 px-3 py-1.5 rounded-full text-sm font-medium hover:bg-teal-200 transition-colors"
                data-testid="active-tag-filter"
              >
                #{filters.tag} <X size={14} />
              </button>
            )}
            {/* View mode toggle */}
            <div className="flex items-center gap-1 border border-gray-200 rounded-lg p-1 ml-auto flex-shrink-0">
              <button
                onClick={() => setViewMode('card')}
                className={`p-1.5 rounded transition-colors ${viewMode === 'card' ? 'bg-levelup-accent text-levelup-darker' : 'text-gray-400 hover:text-gray-600'}`}
                title="Card view"
                data-testid="view-mode-card"
              >
                <LayoutGrid size={16} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 rounded transition-colors ${viewMode === 'list' ? 'bg-levelup-accent text-levelup-darker' : 'text-gray-400 hover:text-gray-600'}`}
                title="List view"
                data-testid="view-mode-list"
              >
                <List size={16} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Smart Search AI Summary Banner */}
      {isSmartResultsActive && smartResults.summary && (
        <div className="bg-levelup-accent/10 border border-levelup-accent/30 rounded-xl p-4 flex items-start space-x-3" data-testid="smart-search-summary">
          <Zap className="text-levelup-accent mt-0.5 flex-shrink-0" size={18} />
          <div>
            <span className="text-xs font-semibold text-levelup-accent uppercase tracking-wide">AI Found {smartResults.total} Course{smartResults.total !== 1 ? 's' : ''}</span>
            <p className="text-sm text-levelup-dark mt-0.5">{smartResults.summary}</p>
          </div>
          <button onClick={clearSmartSearch} className="ml-auto text-gray-400 hover:text-gray-600 flex-shrink-0">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Course Grid / List */}
      {(loading && !smartSearchMode) || smartLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin w-12 h-12 border-4 border-levelup-accent border-t-transparent rounded-full mx-auto"></div>
          <p className="text-gray-500 mt-4">{smartLoading ? 'AI is searching courses...' : t('common.loading')}</p>
        </div>
      ) : displayCourses.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl shadow-md">
          <Search className="mx-auto text-gray-300 mb-4" size={48} />
          <p className="text-gray-500 font-medium">
            {isSmartResultsActive ? 'No courses matched your query. Try different keywords.' : 'No courses found.'}
          </p>
        </div>
      ) : viewMode === 'card' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {displayCourses.map(course => (
            <CourseCard
              key={course.id}
              course={course}
              hasAccess={hasAccess()}
              onView={() => setSelectedCourse(course)}
              relevanceNote={course.relevance_note}
              onTagClick={handleTagClick}
              onQuickSchedule={(course) => {
                setPreselectedCourse(course);
                setShowScheduleModal(true);
              }}
            />
          ))}
        </div>
      ) : (
        /* List View */
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Course Title</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide hidden md:table-cell">Solution Area</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide hidden lg:table-cell">Course Type</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide hidden lg:table-cell">Level</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide hidden xl:table-cell">Language</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {displayCourses.map(course => (
                <tr key={course.id} className="hover:bg-gray-50 transition-colors group">
                  <td className="px-3 py-2">
                    <div className="flex items-start gap-2">
                      <div>
                        <p className="text-sm font-medium text-levelup-dark leading-tight line-clamp-2">{course.title}</p>
                        <div className="flex items-center gap-2 mt-1">
                          {course.hands_on_lab && (
                            <span className="inline-flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                              <FlaskConical size={10} />Lab
                            </span>
                          )}
                          {course.certification_course && (
                            <span className="text-xs text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">Cert</span>
                          )}
                          <span className="text-xs text-gray-400 md:hidden">{course.solution_area}</span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2 hidden md:table-cell">
                    <span className="text-sm text-gray-600">{course.solution_area || '—'}</span>
                  </td>
                  <td className="px-3 py-2 hidden lg:table-cell">
                    <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full">{course.course_type || '—'}</span>
                  </td>
                  <td className="px-3 py-2 hidden lg:table-cell">
                    <span className="text-sm text-gray-600">{course.level || '—'}</span>
                  </td>
                  <td className="px-3 py-2 hidden xl:table-cell">
                    <span className="text-sm text-gray-600">{course.language || '—'}</span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => setSelectedCourse(course)}
                      className="text-xs text-levelup-teal font-medium opacity-0 group-hover:opacity-100 transition-opacity hover:underline"
                      data-testid={`list-view-course-${course.id}`}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 text-xs text-gray-400">
            {displayCourses.length} course{displayCourses.length !== 1 ? 's' : ''}
          </div>
        </div>
      )}

      {selectedCourse && (
        <CourseDetailModal
          course={selectedCourse}
          onClose={() => setSelectedCourse(null)}
          hasAccess={hasAccess()}
          user={user}
          onScheduleWorkshop={(course) => {
            setPreselectedCourse(course);
            setShowScheduleModal(true);
          }}
          onTermsAccept={onTermsAccept}
        />
      )}

      {showScheduleModal && (
        <ScheduleWorkshopModal
          onClose={() => { setShowScheduleModal(false); setPreselectedCourse(null); }}
          onSuccess={() => { setShowScheduleModal(false); setPreselectedCourse(null); }}
          preselectedCourse={preselectedCourse}
        />
      )}
    </div>
  );
};



// ============================================================
// PRIVATE WORKSHOP REQUEST — Modal + Admin Review Page
// ============================================================

// Field component defined outside to prevent remount on state change (avoids focus loss)

export default CourseBrowser;
